import { AgAuthenticationService } from '@ag/vc.ag-core/authentication';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, interval, map, of, take, timeout } from 'rxjs';
import { ProcessCd } from 'src/app/core/models/context';
import { SessionService } from '../../../core/services/session.service';
import { Brand } from '../../../shared/models/brand';
import { DeepLinkingModel } from '../../../shared/models/DeeplinkParams';
import { DistributionChannel } from '../../../shared/models/distributionChannelType';
import { ProductCodeRange } from '../../../shared/models/LeadProducts';
import { SessionKeys } from '../../../shared/models/SessionKeys';
import { RouteNames } from '../../../shared/routing/app-routing.constants';
import { HelperService } from '../../../shared/services/helper.service';
import { RouteConstant } from '../../e-sign/e-sign-routing.constant';
import { ContextService } from '../services/context.service';

@Component({
  selector: 'app-context',
  template: '<div></div>'
})
export class ContextComponent implements OnInit {
  isFromEmail: boolean;
  isAuthenticated = false;
  hasIdToken = false;
  isFromConnective = window.location.href.includes(RouteConstant.fromConnective);
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private helperService: HelperService,
    private contextService : ContextService,
    private readonly agTranslateService: AgTranslateService,
    private readonly authenticationService: AgAuthenticationService,
  ) { }

  ngOnInit(): void | boolean {
    if (window.location.search &&
      Number(this.helperService.getParameterByName('distcha')) === Number(DistributionChannel.AG) && this.helperService.getParameterByName('isDeepLinking')) {
      this.route.queryParams.subscribe(params => {
        const userParams = this.sessionService.getUserParams();
        userParams.distributionChannel = DistributionChannel.AG;
        userParams.partnercode = params.partcd as string|number;
        this.sessionService.setModifiedUserParam(userParams);
        const deepLinkObject = this.createDeeplinkObject();
        this.sessionService.setDeeplinkParams(deepLinkObject);
        this.deepLink(deepLinkObject);
      });
    } else { 
      const processCd = localStorage.getItem('ProcessCd');
      const isPageReload = this.helperService.getSessionValue(SessionKeys.isPageReload) === 'true';
      if(this.router.url.includes('id_token')){
        this.hasIdToken = true;
      };
      if (((processCd === ProcessCd.DirectLogin) || (processCd === ProcessCd.SubscriptionFlow)) && this.hasIdToken) { // if user comes from MSA site after login or registration
        
        if (isPageReload) {
          this.waitForPreservedPathAndRedirect(); // this will call handlePostPostAuth() only if RouteName is not found
        } else {
          this.handlePostAuthRedirections(); // directly called here
        }
  
      }
       else if (this.helperService.getSessionValue(SessionKeys.logoutKey) === 'true') { // if user comes from MSA site after logout
        const isDirectLogin = localStorage.getItem('isDirectLogin');
        if(isDirectLogin === 'true') {
          this.sessionService.updateProcessCd(ProcessCd.DirectLogin);
        } else {
          this.sessionService.updateProcessCd(ProcessCd.SubscriptionFlow);
        }
        this.sessionService.authenticate();
        this.helperService.clearSession(SessionKeys.logoutKey);        
      } else {
        // this if-block will be executed when user comes after GDPR selection from an external AG website
        if (window.location.search?.indexOf('?source=') !== -1 || window.location.search?.indexOf('&source=') !== -1) {
          const urlParams = new URLSearchParams(window.location.search);
          window.location.href = urlParams.get('source');
          return false;
        }
        this.sessionService.clearSession();
        this.route.queryParams.subscribe(params => {
          localStorage.setItem('isDirectLogin', 'false');
          this.sessionService.setUtmParams(params);
          this.sessionService.setUserParams(params); // setting the query params, to be used in later part of the application
          // Always moving to the landing page, route guard will check the query param
          // value and if not satisfied then it will be moved to blocked screen
          this.isFromEmail = this.sessionService.getUserParams().isFromEmail;
          // if the user is redirected from mail he will be redirected to the welcome back screen.
          const createSurveySessionWindow = {
            timeStamp: new Date().getTime(),
            createSurvey: true
          };
          this.helperService.createSession(SessionKeys.startSurveyKey, JSON.stringify(createSurveySessionWindow));
          //this.helperService.clearSession('userSurvey');
          if(this.sessionService.getUserParams().partnercodeRange === ProductCodeRange.Yongo){
            void this.router.navigate([`/${RouteNames.yongoSimulation}`]);
          }
          else{
            if (this.isFromEmail) {
              void this.router.navigate(['/' + RouteNames.simulation + '/' + RouteNames.simulationWelcomeBack]);
            } 
            if (this.isFromConnective){
              return;
            }
            else {
              void this.router.navigate(['/' + RouteNames.simulation]);    
            }
          }          
        });
      }
    }
  }

  
  private waitForPreservedPathAndRedirect(): void {    
    this.helperService.clearSession(SessionKeys.isPageReload);
    interval(100).pipe(
    map(() => localStorage.getItem('PreservedRoutePath')),
    filter(routeURL => !!routeURL),
    take(1),
    timeout({ first: 3000 }),
    catchError(() => {
      this.handlePostAuthRedirections();
      return of(null);
    })
    ).subscribe(routeURL => {
      if (routeURL) {
      this.redirectToStoredRoute(routeURL);
      }
    });
  } 

  private redirectToStoredRoute(routeURL: string): void {
    if (!routeURL) return;
    const cleanedRoute = routeURL.startsWith('/') ? routeURL : `/${routeURL}`;
    this.router.navigateByUrl(cleanedRoute);
    localStorage.removeItem('PreservedRoutePath');
  }
  
  private handlePostAuthRedirections(): void {
   if (!this.authenticationService.accessToken) {
    this.nextActionAfterAuthentication();
   } else if (localStorage.personId && localStorage.uci) {
    this.contextService.retrievePersonDetails();
   } else {
    void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);
   }
  }
  
 
  nextActionAfterAuthentication(): void {
    this.sessionService.authenticate().then((isAuthenticated) => {
      if(isAuthenticated) {
    //       // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          
          this.getTokenValues();
          this.sessionService.setUserLanguage(this.agTranslateService.currentLang);
          this.sessionService.setUserAuthenticationStatus(true);
          // while coming from Mod 1, user will always have the partner details and thus partner logo and url
          if (this.sessionService.getContext().coBrandLogo) {
            const context = this.sessionService.getContext();
            this.sessionService.setContext(Brand.Uppie, context.coBrandLogo, null, null, true, context.partnerUrlFr, context.partnerUrlNl);
          } else { // incase of fallback and no partner details while logging in directly
            this.sessionService.setContext(Brand.Uppie, '');
          }   
          setTimeout(() => {
            this.postActions(); 
          }, 1);         
      } else {
        this.contextService.logAccessTokenErrorInDB('ONE MSA : Unable to get the isAuthenticated value from MSA to verify user authentication after redirection from MSA to LEAD, before fetching the access token and uci from the library');
      }
    }).catch((error) => {      
        this.contextService.logAccessTokenErrorInDB('ONE MSA : Unable to reach the MSA to verify user authentication after redirection from MSA to LEAD, before fetching the access token and uci from the library');
      this.handleAuthFailure();
    });
  }

  postActions(retryCount = 0): void {
    setTimeout(() => {
      const hasData = localStorage.uci && this.authenticationService.isAuthenticated;
      if(hasData) {
        this.contextService.postAuthActions(localStorage.uci);
      } else if (retryCount < 50) { // Retry up to 50 times
        this.postActions(retryCount + 1);
      } else {
        this.nextActionAfterAuthentication();
      }
    }, 100);
  }

  getTokenValues(): void {
    localStorage.uci = this.authenticationService.claims?.appl_user?.trim() ?? this.contextService.getApplUser(this.authenticationService.accessToken)?.trim();
    this.agTranslateService.currentLang = this.authenticationService.user_language;
  }   

  handleAuthFailure(): void {       
      if(this.helperService.getSessionValue(SessionKeys.isRetryOver) === 'true') {
        void this.router.navigate(['/' + RouteNames.ComeBackLater]);
      } else {        
      this.helperService.clearSession(SessionKeys.isFromComeBackLater);
      void this.router.navigate(['/' + RouteNames.ErrorScreen], { state: { isAuthFlowFailure: 'true' } });
      }
  }

  createDeeplinkObject(): DeepLinkingModel{
    const deepLinkObject : DeepLinkingModel = {
      route : this.helperService.getParameterByName('ScreenName'),
      isDeepLinking : this.helperService.getParameterByName('isDeepLinking'),
      simulationId : this.helperService.getParameterByName('simulationId') ? Number(this.helperService.getParameterByName('simulationId')) : 0,
      planId : this.helperService.getParameterByName('planId') ? Number(this.helperService.getParameterByName('planId')) : 0,
      amount : this.helperService.getParameterByName('amount') ? Number(this.helperService.getParameterByName('amount')) : 0   
    };
    return deepLinkObject;
  }

  deepLink(deepLinkObject: DeepLinkingModel): void {    
    if (this.authenticationService.isAuthenticated) {
      // route for desired location will go here
      this.contextService.navigateToDeepLink(deepLinkObject);
    } else {      
      //this.helperService.createSession('deepLink', JSON.stringify(deepLinkObject));
      void this.router.navigate([`/${RouteNames.authentication}/${RouteNames.login}`]);
    }
  }


}